import React, { useCallback, useRef, useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Heading from '@tiptap/extension-heading';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import {
  Bold,
  Italic,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Heading1,
  Heading2,
  Heading3,
  Link as LinkIcon,
  List,
  ListOrdered,
  Check,
  Loader2,
  AlertCircle,
  Save
} from 'lucide-react';

interface EditorProps {
  initialContent: string;
  onUpdate: (content: string) => void;
  saveStatus: 'saved' | 'saving' | 'error';
}

const AUTO_SAVE_INTERVAL = 30000; // 30 seconds

const SaveStatus = ({ status }: { status: 'saving' | 'saved' | 'error' }) => {
  const [visible, setVisible] = useState(true);
  
  useEffect(() => {
    setVisible(true); // Show status whenever it changes
    
    // Only hide after delay if status is 'saved'
    if (status === 'saved') {
      const timer = setTimeout(() => {
        setVisible(false);
      }, 2000); // Hide after 2 seconds
      
      return () => clearTimeout(timer);
    }
  }, [status]);

  const statusConfig = {
    saving: { 
      text: 'Saving...',
      className: 'text-yellow-600',
      icon: <Loader2 className="w-4 h-4 animate-spin" />
    },
    saved: { 
      text: 'Saved',
      className: 'text-green-600',
      icon: <Check className="w-4 h-4" />
    },
    error: { 
      text: 'Error saving',
      className: 'text-red-600',
      icon: <AlertCircle className="w-4 h-4" />
    },
  };

  const config = statusConfig[status];

  return (
    <div 
      className={`flex items-center gap-1 text-sm transition-opacity duration-300 ${
        visible ? 'opacity-100' : 'opacity-0'
      } ${config.className}`}
    >
      {config.icon}
      {config.text}
    </div>
  );
};

const MenuBar = ({ editor, onSave, saveStatus }: { editor: any; onSave: () => void; saveStatus: 'saved' | 'saving' | 'error' }) => {
  if (!editor) {
    return null;
  }

  const setLink = () => {
    const url = window.prompt('URL:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <div className="border-b border-gray-200 p-2 flex flex-wrap gap-1 items-center">
      <div className="flex flex-wrap gap-1 flex-1">
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-2 rounded hover:bg-gray-100 ${
            editor.isActive('bold') ? 'bg-gray-100' : ''
          }`}
        >
          <Bold className="w-5 h-5" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-2 rounded hover:bg-gray-100 ${
            editor.isActive('italic') ? 'bg-gray-100' : ''
          }`}
        >
          <Italic className="w-5 h-5" />
        </button>
        <div className="w-px h-8 bg-gray-200 mx-1 self-center" />
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={`p-2 rounded hover:bg-gray-100 ${
            editor.isActive('heading', { level: 1 }) ? 'bg-gray-100' : ''
          }`}
        >
          <Heading1 className="w-5 h-5" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`p-2 rounded hover:bg-gray-100 ${
            editor.isActive('heading', { level: 2 }) ? 'bg-gray-100' : ''
          }`}
        >
          <Heading2 className="w-5 h-5" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={`p-2 rounded hover:bg-gray-100 ${
            editor.isActive('heading', { level: 3 }) ? 'bg-gray-100' : ''
          }`}
        >
          <Heading3 className="w-5 h-5" />
        </button>
        <div className="w-px h-8 bg-gray-200 mx-1 self-center" />
        <button
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={`p-2 rounded hover:bg-gray-100 ${
            editor.isActive({ textAlign: 'left' }) ? 'bg-gray-100' : ''
          }`}
        >
          <AlignLeft className="w-5 h-5" />
        </button>
        <button
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={`p-2 rounded hover:bg-gray-100 ${
            editor.isActive({ textAlign: 'center' }) ? 'bg-gray-100' : ''
          }`}
        >
          <AlignCenter className="w-5 h-5" />
        </button>
        <button
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className={`p-2 rounded hover:bg-gray-100 ${
            editor.isActive({ textAlign: 'right' }) ? 'bg-gray-100' : ''
          }`}
        >
          <AlignRight className="w-5 h-5" />
        </button>
        <div className="w-px h-8 bg-gray-200 mx-1 self-center" />
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-2 rounded hover:bg-gray-100 ${
            editor.isActive('bulletList') ? 'bg-gray-100' : ''
          }`}
        >
          <List className="w-5 h-5" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`p-2 rounded hover:bg-gray-100 ${
            editor.isActive('orderedList') ? 'bg-gray-100' : ''
          }`}
        >
          <ListOrdered className="w-5 h-5" />
        </button>
        <div className="w-px h-8 bg-gray-200 mx-1 self-center" />
        <button
          onClick={setLink}
          className={`p-2 rounded hover:bg-gray-100 ${
            editor.isActive('link') ? 'bg-gray-100' : ''
          }`}
        >
          <LinkIcon className="w-5 h-5" />
        </button>
      </div>

      <div className="flex items-center gap-4">
        <SaveStatus status={saveStatus} />
        <button
          onClick={onSave}
          className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center gap-1"
          title="Save changes"
        >
          <Save className="w-4 h-4" />
          Save
        </button>
      </div>
    </div>
  );
};

const WordCount = ({ editor }: { editor: any }) => {
  if (!editor) {
    return null;
  }

  const words = editor.getText().split(/\s+/).filter(word => word.length > 0).length;
  const characters = editor.getText().length;

  return (
    <div className="text-sm text-gray-500 flex gap-4">
      <span>{words} {words === 1 ? 'word' : 'words'}</span>
      <span>{characters} {characters === 1 ? 'character' : 'characters'}</span>
    </div>
  );
};

export default function Editor({ initialContent, onUpdate, saveStatus }: EditorProps) {
  const hasUnsavedChanges = useRef(false);
  const autoSaveTimer = useRef<NodeJS.Timeout | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Heading.configure({
        levels: [1, 2, 3],
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Link.configure({
        openOnClick: false,
      }),
    ],
    content: initialContent || '<p>Start writing your story...</p>',
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none min-h-[200px]',
      },
    },
    onUpdate: ({ editor }) => {
      hasUnsavedChanges.current = true;
    },
  });

  const save = useCallback(() => {
    if (editor && hasUnsavedChanges.current) {
      const content = editor.getHTML();
      onUpdate(content);
      hasUnsavedChanges.current = false;
    }
  }, [editor, onUpdate]);

  // Set up auto-save
  useEffect(() => {
    autoSaveTimer.current = setInterval(() => {
      if (hasUnsavedChanges.current) {
        save();
      }
    }, AUTO_SAVE_INTERVAL);

    return () => {
      if (autoSaveTimer.current) {
        clearInterval(autoSaveTimer.current);
      }
    };
  }, [save]);

  return (
    <div className="border rounded-lg bg-white shadow-sm">
      <MenuBar editor={editor} onSave={save} saveStatus={saveStatus} />
      <div className="p-4">
        <EditorContent editor={editor} />
      </div>
      <div className="px-4 pb-2">
        <WordCount editor={editor} />
      </div>
    </div>
  );
}